/* Light & Wellness Co. — wishlist
 *
 * Storage model
 *   Guests           : localStorage only.
 *   Logged-in buyers : localStorage is still the working copy (instant UI), and every
 *                      change is pushed to the customer's `custom.wishlist` metafield so
 *                      the list follows them across devices.
 *
 * The sync layer stays dormant until window.LWWishlist.syncUrl is set, so the local
 * wishlist works on its own with no backend deployed.
 */
(function () {
  'use strict';

  var CFG = window.LWWishlist || {};
  var KEY = 'lw_wishlist_v1';
  var listeners = [];
  var pushTimer = null;

  /* ---------------------------------------------------------------- storage */

  function read() {
    try {
      var raw = localStorage.getItem(KEY);
      var val = raw ? JSON.parse(raw) : [];
      return Array.isArray(val) ? val.filter(function (i) { return i && i.pid; }) : [];
    } catch (e) { return []; }
  }

  function write(items) {
    try { localStorage.setItem(KEY, JSON.stringify(items)); } catch (e) {}
    listeners.forEach(function (fn) { try { fn(items); } catch (e) {} });
  }

  function has(pid) {
    return read().some(function (i) { return String(i.pid) === String(pid); });
  }

  /* ------------------------------------------------------------------- sync */

  function pushToServer() {
    if (!CFG.syncUrl || !CFG.customerId || !CFG.sig) return;
    clearTimeout(pushTimer);
    pushTimer = setTimeout(function () {
      var ids = read().map(function (i) { return String(i.pid); });
      fetch(CFG.syncUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customerId: CFG.customerId, sig: CFG.sig, productIds: ids })
      }).catch(function () { /* offline or endpoint down — local copy is still correct */ });
    }, 600);
  }

  /* Merge the server's copy (rendered into the page by Liquid) with whatever this
     device already had. Union by product id: nothing a customer saved is ever dropped
     just because they logged in on a new device. */
  function mergeServer() {
    if (!CFG.customerId || !Array.isArray(CFG.serverItems)) return;
    var local = read();
    var seen = {};
    local.forEach(function (i) { seen[String(i.pid)] = true; });

    var merged = local.slice();
    var added = 0;
    CFG.serverItems.forEach(function (i) {
      if (i && i.pid && !seen[String(i.pid)]) { merged.push(i); added++; }
    });

    var serverIds = CFG.serverItems.map(function (i) { return String(i.pid); }).sort().join(',');
    var mergedIds = merged.map(function (i) { return String(i.pid); }).sort().join(',');

    if (added) write(merged);
    if (mergedIds !== serverIds) pushToServer();
  }

  /* -------------------------------------------------------------------- api */

  var api = {
    all: read,
    count: function () { return read().length; },
    has: has,

    add: function (item) {
      if (!item || !item.pid || has(item.pid)) return read();
      var items = read();
      item.addedAt = Date.now();
      items.unshift(item);
      write(items);
      pushToServer();
      return items;
    },

    remove: function (pid) {
      var items = read().filter(function (i) { return String(i.pid) !== String(pid); });
      write(items);
      pushToServer();
      return items;
    },

    toggle: function (item) {
      return has(item.pid) ? (api.remove(item.pid), false) : (api.add(item), true);
    },

    onChange: function (fn) { listeners.push(fn); return fn; }
  };

  window.LWWishlistAPI = api;

  /* --------------------------------------------------------------------- ui */

  function paintButton(btn) {
    var on = has(btn.getAttribute('data-wishlist-pid'));
    btn.classList.toggle('is-active', on);
    btn.setAttribute('aria-pressed', on ? 'true' : 'false');
    var label = on ? 'Remove from wishlist' : 'Save to wishlist';
    btn.setAttribute('aria-label', label);
    btn.setAttribute('title', label);
    var text = btn.querySelector('[data-wishlist-label]');
    if (text) text.textContent = on ? 'Saved to wishlist' : 'Save to wishlist';
  }

  function paintAll() {
    document.querySelectorAll('[data-wishlist-toggle]').forEach(paintButton);
    var n = api.count();
    document.querySelectorAll('[data-wishlist-count]').forEach(function (el) {
      el.textContent = n;
      el.hidden = n === 0;
    });
  }

  function itemFromButton(btn) {
    return {
      pid:   btn.getAttribute('data-wishlist-pid'),
      h:     btn.getAttribute('data-wishlist-handle'),
      t:     btn.getAttribute('data-wishlist-title'),
      img:   btn.getAttribute('data-wishlist-image'),
      p:     btn.getAttribute('data-wishlist-price'),
      u:     btn.getAttribute('data-wishlist-url')
    };
  }

  /* Capture phase, deliberately. theme.js has a bubble-phase listener on document that
     navigates to the product whenever anything inside .product-card is clicked, and it
     only exempts [data-add-to-cart] and [data-quick-view]. Intercepting here stops the
     event before it ever reaches that handler, so the heart doesn't also open the PDP. */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-wishlist-toggle]');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();
    api.toggle(itemFromButton(btn));
    btn.classList.add('just-toggled');
    setTimeout(function () { btn.classList.remove('just-toggled'); }, 320);
  }, true);

  api.onChange(paintAll);
  api.repaint = paintAll;

  function init() {
    mergeServer();
    paintAll();
    paintRecent();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }


  /* ------------------------------------------------------------ rendering */

  function money(cents) {
    var n = parseInt(cents, 10);
    return isNaN(n) ? '' : '$' + (n / 100).toFixed(2);
  }

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  /* One card renderer shared by the drawer and the /pages/wishlist fallback, so the two
     can never drift apart. */
  function cardHTML(item, variant) {
    var compact = variant === 'compact';
    return '' +
      '<article class="wl-card' + (compact ? ' wl-card--compact' : '') + '" data-wl-item="' + esc(item.pid) + '">' +
        '<a class="wl-card__media" href="' + esc(item.u) + '">' +
          (item.img ? '<img src="' + esc(item.img) + '" alt="' + esc(item.t) + '" loading="lazy" width="200" height="200">' : '') +
        '</a>' +
        '<div class="wl-card__info">' +
          '<a class="wl-card__title" href="' + esc(item.u) + '">' + esc(item.t) + '</a>' +
          '<div class="wl-card__price" data-wl-price>' + money(item.p) + '</div>' +
          '<div class="wl-card__actions" data-wl-actions></div>' +
        '</div>' +
        '<button type="button" class="wl-card__remove" data-wl-remove="' + esc(item.pid) + '" aria-label="Remove ' + esc(item.t) + ' from wishlist">' +
          '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M18 6L6 18M6 6l12 12"/></svg>' +
        '</button>' +
      '</article>';
  }

  /* Cached price/title can be months old, so re-read each product and correct the card.
     This is also where the current variant id and availability come from. */
  function refreshCard(root, item) {
    if (!item.h) return;
    fetch('/products/' + encodeURIComponent(item.h) + '.js')
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (p) {
        if (!p) return;
        var el = root.querySelector('[data-wl-item="' + item.pid + '"]');
        if (!el) return;
        var priceEl = el.querySelector('[data-wl-price]');
        if (priceEl) priceEl.textContent = (p.price_varies ? 'From ' : '') + money(p.price);
        var actions = el.querySelector('[data-wl-actions]');
        if (!actions) return;
        if (!p.available) {
          actions.innerHTML = '<span class="wl-card__soldout">Sold out</span>';
        } else if (p.variants && p.variants.length > 1) {
          actions.innerHTML = '<a class="btn btn--outline wl-card__atc" href="' + esc(p.url) + '">Choose options</a>';
        } else if (p.variants && p.variants[0]) {
          actions.innerHTML = '<button type="button" class="btn btn--primary wl-card__atc" data-add-to-cart data-variant-id="' + p.variants[0].id + '">Add to cart</button>';
        }
      })
      .catch(function () {});
  }

  api.renderInto = function (root, variant) {
    if (!root) return 0;
    var items = read();
    root.innerHTML = items.map(function (i) { return cardHTML(i, variant); }).join('');
    items.forEach(function (i) { refreshCard(root, i); });
    return items.length;
  };

  document.addEventListener('click', function (e) {
    var rm = e.target.closest('[data-wl-remove]');
    if (!rm) return;
    e.preventDefault();
    api.remove(rm.getAttribute('data-wl-remove'));
  });

  /* --------------------------------------------------------------- drawer */

  var drawer, lastFocus = null;

  function drawerEls() {
    drawer = drawer || document.querySelector('.wl-drawer');
    return drawer;
  }

  function paintDrawer() {
    var d = drawerEls(); if (!d) return;
    var list  = d.querySelector('[data-wl-list]');
    var empty = d.querySelector('[data-wl-drawer-empty]');
    var count = d.querySelector('[data-wl-drawer-count]');
    var n = api.renderInto(list, 'compact');
    if (empty) empty.hidden = n !== 0;
    if (list)  list.hidden  = n === 0;
    if (count) count.textContent = n === 0 ? '' : n + (n === 1 ? ' item' : ' items');
  }

  function openDrawer() {
    var d = drawerEls(); if (!d) return;
    lastFocus = document.activeElement;
    paintDrawer();
    d.classList.add('is-open');
    d.setAttribute('aria-hidden', 'false');
    d.removeAttribute('inert');
    document.documentElement.style.overflow = 'hidden';
    /* inert is removed synchronously above, but the element isn't focusable until the
       browser has processed that, so defer a frame before moving focus into the dialog. */
    requestAnimationFrame(function () {
      var close = d.querySelector('button[data-wl-drawer-close]');
      if (close) close.focus();
    });
  }

  function closeDrawer() {
    var d = drawerEls(); if (!d) return;
    d.classList.remove('is-open');
    d.setAttribute('aria-hidden', 'true');
    d.setAttribute('inert', '');
    document.documentElement.style.overflow = '';
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  }

  api.openDrawer = openDrawer;
  api.closeDrawer = closeDrawer;

  document.addEventListener('click', function (e) {
    if (e.target.closest('[data-search-toggle]')) { setTimeout(paintRecent, 60); }
    if (e.target.closest('[data-wl-drawer-open]')) { e.preventDefault(); openDrawer(); return; }
    if (e.target.closest('[data-wl-drawer-close]')) { e.preventDefault(); closeDrawer(); }
  });

  document.addEventListener('keydown', function (e) {
    var d = drawerEls();
    if (!d || !d.classList.contains('is-open')) return;
    if (e.key === 'Escape') { closeDrawer(); return; }
    if (e.key !== 'Tab') return;
    var f = d.querySelectorAll('a[href], button:not([disabled]), input, [tabindex]:not([tabindex="-1"])');
    if (!f.length) return;
    var first = f[0], last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  });

  api.onChange(function () {
    var d = drawerEls();
    if (d && d.classList.contains('is-open')) paintDrawer();
  });


  /* ------------------------------------------------- recently viewed strip
     Reads the same store theme.js already writes on product pages
     ('lwco-recently-viewed'), and paints a compact strip into any
     [data-recent-mount]. Recording stays where it is; this only renders, so the
     two can't disagree about what you looked at. */

  function recentItems(limit) {
    var out = [];
    try { out = JSON.parse(localStorage.getItem('lwco-recently-viewed') || '[]'); }
    catch (e) { out = []; }
    return Array.isArray(out) ? out.slice(0, limit || 6) : [];
  }

  function paintRecent() {
    var mounts = document.querySelectorAll('[data-recent-mount]');
    if (!mounts.length) return;
    var items = recentItems(6);
    mounts.forEach(function (mount) {
      var track = mount.querySelector('[data-recent-track]');
      if (!track) return;
      if (!items.length) { mount.dataset.hasItems = '0'; mount.hidden = true; return; }
      track.innerHTML = items.map(function (i) {
        return '' +
          '<a class="recent-chip" href="' + esc(i.url) + '">' +
            (i.image ? '<span class="recent-chip__img"><img src="' + esc(i.image) + '" alt="" loading="lazy" width="60" height="60"></span>' : '') +
            '<span class="recent-chip__body">' +
              '<span class="recent-chip__title">' + esc(i.title) + '</span>' +
              '<span class="recent-chip__price">' + (i.priceVaries ? 'From ' : '') + money(i.price) + '</span>' +
            '</span>' +
          '</a>';
      }).join('');
      mount.dataset.hasItems = '1';
      mount.hidden = searchActive();
    });
  }

  /* Once someone starts typing, recently-viewed is noise — the results are what they want. */
  function searchActive() {
    var si = document.querySelector('[data-search-input]');
    return !!(si && si.value && si.value.trim().length);
  }

  function syncRecentVisibility() {
    document.querySelectorAll('.search-drawer__recent').forEach(function (m) {
      m.hidden = m.dataset.hasItems !== '1' || searchActive();
    });
  }

  document.addEventListener('input', function (e) {
    if (e.target && e.target.matches('[data-search-input]')) syncRecentVisibility();
  });

  api.paintRecent = paintRecent;

  /* keep tabs in step */
  window.addEventListener('storage', function (e) { if (e.key === KEY) paintAll(); });
})();
